import { retrieveToken } from '../storage';
import {
  wrapFetch,
  wrapFetchWithDetailedErrors,
  ApiError,
  apiFetch,
  apiError,
  isCodiceErrore,
  OPERAZIONE_NON_CONSENTITA,
} from './api';

const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;

// The attachment title is already taken in the same folder. A code, not a
// sentence: `message` for this refusal is «Record gia' presente», the generic
// bucket of the exception handler, which does not tell this conflict apart from
// any other 409 — `ERR-001` («Aggiornamento manuale in corso») arrives with the
// same status. On the status alone the panel would offer to overwrite a document
// while the backend is halfway through an update.
export const DUPLICATE_TITLE_CODE = 'ERR-005';

// Read the same way `api.js` decides what is a code at all, so the two cannot
// disagree: an entry the server wrote as `err-005` would otherwise be no
// conflict here and the sentence to display there, which is the code itself.
const isDuplicateTitleCode = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase() === DUPLICATE_TITLE_CODE;

// The same sentence under the confirmation and in the form's Titolo field: the
// reason has to survive dismissing the confirmation. It states a fact rather
// than asking for another title, because overwriting is one of the two ways out.
export const DUPLICATE_TITLE_MESSAGE =
  'Esiste già un allegato con questo titolo in questa cartella.';

/**
 * The duplicate-title refusal carries a positional `details`:
 * `[code, titolo of the document holding it, the date it was uploaded]`. The
 * server builds it from `getTitolo()` and `getDataCaricamento()`, so neither is
 * a file name nor a modification date. It travels whole because the
 * confirmation has to say *which* document is about to be replaced, and
 * `ApiError` has nowhere to put it.
 */
class DuplicateAttachmentError extends ApiError {
  constructor(status, message, details) {
    super(status, message);
    this.details = details ?? [];
  }
}

/**
 * `{ existingTitle, uploadedDate }` when the refusal is a duplicate title, `null`
 * for every other error. The only place that knows the shape of `details`: rewriting
 * every error response to RFC 9457 is planned, with no date, and this is the
 * function that changes that day.
 *
 * The gate is the status plus the *first* entry, the way the contract lays
 * `details` out and the way bs-safe-app reads it: an `ERR-005` appearing further
 * down some other refusal's details is not this conflict.
 *
 * Both values are individually optional: an answer carrying the code and no
 * title is still a conflict, and the confirmation appears naming no document.
 * The title is dropped when it is itself a code, which is what a one-element
 * `details` amounts to.
 */
export const duplicateTitleConflict = (error) => {
  const details = error?.details;
  if (error?.status !== 409) return null;
  if (!Array.isArray(details)) return null;
  if (!isDuplicateTitleCode(details[0])) return null;

  const existingTitle = details[1];
  return {
    existingTitle:
      existingTitle && !isCodiceErrore(existingTitle) ? existingTitle : null,
    uploadedDate: details[2] ?? null,
  };
};

const defaultErrorFunction = apiError();

// Not `apiError`: on these routes a 403 says the operator may not touch the
// document at all, so it wins over whatever sentence the server sent with it.
const detailedErrorFunction = (status, detailedMessage) => {
  if (status === 403) {
    return new ApiError(status, OPERAZIONE_NON_CONSENTITA);
  }
  return new ApiError(status, detailedMessage);
};

const uploadErrorFunction = (status, detailedMessage, details) => {
  if (status === 403) {
    return new ApiError(status, OPERAZIONE_NON_CONSENTITA);
  }
  if (status === 413) {
    return new ApiError(
      status,
      'Caricamento fallito: il formato o la dimensione del file non sono corretti.',
    );
  }
  if (Array.isArray(details) && isDuplicateTitleCode(details[0])) {
    // The sentence, not `detailedMessage`: whoever reads `details` takes the
    // first entry that is not a code, and here that entry is the titolo of the
    // document already there. A refusal reaching the generic error dialog — the
    // 409 on a save that already asked to overwrite — would tell the operator
    // «Verbale» and nothing else.
    return new DuplicateAttachmentError(
      status,
      DUPLICATE_TITLE_MESSAGE,
      details,
    );
  }
  return new ApiError(status, detailedMessage);
};

/**
 * One level of the documents tree, or a flat list of documents.
 *
 * `archivio` is `ATTIVI`, `ARCHIVIATI` or `TUTTI` and filters the documents,
 * never the folders: with `ARCHIVIATI` the server keeps only the folders that
 * hold an archived document somewhere below them. Left out, the server reads
 * `ATTIVI`.
 *
 * `scadenza` is one of the presets `IN_SCADENZA_30`, `IN_SCADENZA_7`,
 * `SCADUTI_30`, `SCADUTI`, counted in days from today in Europe/Rome by the
 * server. With it the answer is flat: documents only, from the whole tree,
 * archived ones always left out, `cartella` ignored, `titolo` still applied.
 */
export const getDocumentiPerEsercizio = (
  esercizioId,
  { cartella, sort, titolo, archivio, scadenza } = {},
) => {
  const token = retrieveToken();
  let PATH = `/api/documenti/esercizio/${esercizioId}/allegati`;
  const params = [];
  if (cartella != null) {
    params.push(`cartella=${cartella}`);
  }
  if (titolo) {
    params.push(`titolo=${encodeURIComponent(titolo)}`);
  }
  if (sort) {
    params.push(`sort=${sort}`);
  }
  if (archivio) {
    params.push(`archivio=${archivio}`);
  }
  if (scadenza) {
    params.push(`scadenza=${scadenza}`);
  }
  if (params.length > 0) {
    PATH += '?' + params.join('&');
  }
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
    }),
    defaultErrorFunction,
  );
};

export const getDocumentoDettaglio = (documentoId) => {
  const token = retrieveToken();
  const URL = BASE_URL + `/api/documenti/${documentoId}/dettagli`;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
    }),
    defaultErrorFunction,
  );
};

export const uploadDocumentoEsercizio = (formData) => {
  const token = retrieveToken();
  const URL = BASE_URL + '/api/v2/documenti/esercizio/upload';
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
      body: formData,
    }),
    uploadErrorFunction,
  );
};

export const updateDocumentoEsercizio = (documentoId, formData) => {
  const token = retrieveToken();
  const URL =
    BASE_URL + `/api/v2/documenti/esercizio/update?documentoId=${documentoId}`;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
      body: formData,
    }),
    uploadErrorFunction,
  );
};

export const deleteDocumentoEsercizio = (documentoId) => {
  const token = retrieveToken();
  const URL = BASE_URL + `/api/documenti/esercizio/doc/${documentoId}`;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'DELETE',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
    }),
    detailedErrorFunction,
  );
};

/**
 * Archives a document (`archiviato` true) or restores it (false). Both are
 * idempotent on the server: archiving an archived document keeps the day it
 * was first archived. Answers with the document's detail, `dataArchiviazione`
 * included. On a 403 the operator may not touch the document; a 404 carries the
 * server's sentence, for a document that no longer exists.
 */
export const archiviaDocumento = (documentoId, archiviato) => {
  const token = retrieveToken();
  const URL =
    BASE_URL + `/api/v2/documenti/esercizio/${documentoId}/archiviazione`;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PATCH',
      headers: {
        Authorization: 'Bearer '.concat(token),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ archiviato }),
    }),
    detailedErrorFunction,
  );
};

export const getDocumentoFile = (documentoId) => {
  const token = retrieveToken();
  const URL = BASE_URL + `/api/documenti/${documentoId}`;
  return apiFetch(URL, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer '.concat(token),
    },
    credentials: 'include',
  }).then((response) => {
    if (response.ok) {
      return response.blob();
    }
    throw new ApiError(response.status, 'Impossibile scaricare il file.');
  });
};

/**
 * The JSON body of a folder write. `repartiVisibilitaIds` is three-valued on
 * the server — absent means "default on create, unchanged on update", `[]`
 * means the whole structure, a list means those reparti — and `JSON.stringify`
 * already drops an `undefined` key, so leaving the key in place is what keeps
 * the three apart. A caller that has nothing to say about visibility passes
 * nothing.
 */
const corpoCartella = ({ titolo, cartellaSuperioreId, repartiVisibilitaIds }) =>
  JSON.stringify({ titolo, cartellaSuperioreId, repartiVisibilitaIds });

export const createCartella = (esercizioId, cartella) => {
  const token = retrieveToken();
  const URL = BASE_URL + `/api/documenti/esercizio/${esercizioId}/cartelle`;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer '.concat(token),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: corpoCartella(cartella),
    }),
    detailedErrorFunction,
  );
};

export const updateCartella = (esercizioId, cartellaId, cartella) => {
  const token = retrieveToken();
  const URL =
    BASE_URL + `/api/documenti/esercizio/${esercizioId}/cartelle/${cartellaId}`;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: 'Bearer '.concat(token),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: corpoCartella(cartella),
    }),
    detailedErrorFunction,
  );
};

export const deleteCartella = (esercizioId, cartellaId) => {
  const token = retrieveToken();
  const URL =
    BASE_URL + `/api/documenti/esercizio/${esercizioId}/cartelle/${cartellaId}`;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'DELETE',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
    }),
    detailedErrorFunction,
  );
};

export const getCartelleAnnidate = (esercizioId) => {
  const token = retrieveToken();
  const URL = BASE_URL + `/api/documenti/esercizio/${esercizioId}/cartelle`;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
    }),
    defaultErrorFunction,
  );
};

/**
 * The reparti an attachment or a folder can be restricted to: `[{ id, nome }]`,
 * the non-default reparti of the esercizio's current copy (its gestione
 * operativa, else its last published revision), sorted by name. Every
 * publication reclones the reparti with new ids, so a restriction stored on an
 * element matches these by `nome`, never by `id`.
 */
export const getRepartiVisibilita = (esercizioId) => {
  const token = retrieveToken();
  const URL =
    BASE_URL + `/api/documenti/esercizio/${esercizioId}/reparti-visibilita`;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer '.concat(token),
      },
      credentials: 'include',
    }),
    defaultErrorFunction,
  );
};
